import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Theme, Tooltip, Typography } from '@material-ui/core'
import LabelChip from '@src/components/LabelChip'
import clsx from 'clsx'
import { FormattedMessage, FormattedNumber } from 'react-intl'
import { Linq } from '@src/components/Linq/Linq'
import { promoCampaignsRoute } from '@src/routes'

const useStyles = makeStyles((theme: Theme) => ({
  completedChip: {
    justifyContent: 'flex-start',
    '& .MuiChip-root': {
      backgroundColor: '#C1FFCA',
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(0),
      height: '26px',
    },
  },
  clickableChip: {
    '& .MuiChip-root': {
      cursor: 'pointer',
    },
  },
  completedChipLabel: {
    fontSize: theme.typography.pxToRem(12),
    fontWeight: theme.typography.fontWeightMedium,
  },
  tooltip: {
    backgroundColor: theme.palette.common.white,
    width: theme.typography.pxToRem(320),
    fontSize: theme.typography.pxToRem(16),
    boxShadow: '0 4px 6px 0 rgba(0, 0, 0, 0.16)',
    padding: theme.spacing(3, 4),
    top: '16px',
  },
  tooltipWrapper: {
    display: 'inline-block',
  },
  arrow: {
    color: theme.palette.common.white,
    fontSize: theme.typography.pxToRem(15),
    '&::before': {
      boxShadow: '0px 0px 2px rgba(0,0,0,0.2)',
    },
  },
  promoTitle: {
    fontWeight: theme.typography.fontWeightBold,
  },
  promoDescription: {
    marginBottom: theme.spacing(3),
  },
  shopProductsLink: {
    fontWeight: theme.typography.fontWeightBold,
  },
}))

interface SMSMBadgeProps {
  promoCode?: string
  promoId?: string
  discount?: number
  qualifyingAmount?: number
  currency?: string
  className?: string
  showTooltip?: boolean
}

const SMSMBadge: React.FC<SMSMBadgeProps> = ({
  promoCode,
  promoId,
  discount,
  qualifyingAmount,
  currency,
  className,
  showTooltip = false,
}) => {
  const classes = useStyles()

  const tooltipContent = (
    <>
      {discount && (
        <Typography className={classes.promoTitle}>
          <FormattedMessage
            id="SPEND_AND_SAVE_DISCOUNT"
            defaultMessage="Spend & Save {discount}"
            values={{
              discount: (
                <FormattedNumber
                  minimumFractionDigits={0}
                  style="percent"
                  value={discount / 100}
                />
              ),
            }}
          />
        </Typography>
      )}
      {qualifyingAmount && discount && (
        <Typography className={classes.promoDescription}>
          <FormattedMessage
            id="ELIGIBLE_PRODUCTS"
            defaultMessage="Spend {qualifyingAmount} on eligible products to save {discount}."
            values={{
              discount: (
                <FormattedNumber
                  minimumFractionDigits={0}
                  style="percent"
                  value={discount / 100}
                />
              ),
              qualifyingAmount: (
                <FormattedNumber
                  currency={currency}
                  maximumFractionDigits={0}
                  minimumFractionDigits={0}
                  style="currency"
                  value={qualifyingAmount}
                />
              ),
            }}
          />{' '}
          <FormattedMessage
            id="SPEND_AND_SAVE_APPLY_DISCOUNT"
            defaultMessage="Click “Apply Discount” in cart to redeem."
          />
        </Typography>
      )}
      {promoCode && promoId && (
        <Linq
          href={promoCampaignsRoute.smsm(promoCode, promoId)}
          className={classes.shopProductsLink}
          rel="nofollow"
        >
          <FormattedMessage
            id="SHOP_ELIGIBLE_PRODUCTS"
            defaultMessage="Shop Eligible Products"
          />
        </Linq>
      )}
    </>
  )

  const labelChip = (
    <LabelChip
      label={
        <Typography className={classes.completedChipLabel}>
          <FormattedMessage
            id="SPEND_AND_SAVE_BADGE"
            defaultMessage="Spend & Save"
          />
        </Typography>
      }
      className={clsx(classes.completedChip, className, {
        [classes.clickableChip]: showTooltip,
      })}
    />
  )

  return (
    <div data-testid="smsm-badge">
      {showTooltip ? (
        <Tooltip
          arrow
          classes={{
            tooltip: classes.tooltip,
            arrow: classes.arrow,
          }}
          placement="top"
          interactive
          title={tooltipContent}
        >
          <div className={classes.tooltipWrapper}>{labelChip}</div>
        </Tooltip>
      ) : (
        labelChip
      )}
    </div>
  )
}

export default SMSMBadge
